'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { Card } from '@/components/cms/Card';
import { MediaWrapper } from '@/components/cms/MediaWrapper';
import { Icon } from '@/components/ui/Icon';
import { useSliderEntryEffect } from '@/hooks/useSliderEntryEffect';
import type { CMSContentGroup, CMSImage } from '@/lib/cms/types';
import type { ContentfulCardFragment, ContentGroupFragment } from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './SliderFullwidthGallery.module.scss';

// Import Swiper styles
import 'swiper/css';

export interface SliderFullwidthGalleryProps {
  /** Contentful ContentGroup data with Card items or adapted CMSContentGroup */
  data: ContentGroupFragment | CMSContentGroup;
  /** Optional extra data for cards */
  extraData?: Record<string, unknown>;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

/**
 * SliderFullwidthGallery - Full-width card carousel
 *
 * A Swiper-based gallery slider that spans the full width of the viewport.
 * Features custom navigation arrows and scrollbar positioned below the slides.
 * Supports both Card items and ImageWithFocalPoint items.
 */
export function SliderFullwidthGallery({ data, cmsIndex, className }: SliderFullwidthGalleryProps) {
  const aria = useAriaLabels();
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [swiperProgress, setSwiperProgress] = useState(0);

  // Support both raw Contentful format (itemsCollection) and adapted format (items)
  const adaptedData = data as CMSContentGroup;
  const rawData = data as ContentGroupFragment;
  const items: unknown[] =
    adaptedData.items && Array.isArray(adaptedData.items)
      ? adaptedData.items
      : filterCollection(rawData.itemsCollection);

  const effectIndices = useMemo(() => {
    const indices = new Set<number>();
    items.forEach((item, i) => {
      if ((item as { entryEffect?: boolean }).entryEffect) indices.add(i);
    });
    return indices;
  }, [items]);

  const { containerRef, isRevealed } = useSliderEntryEffect({
    swiper: swiperInstance,
    totalItems: items.length,
    effectIndices,
  });

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `sliderfullwidthgallery-${cmsIndex}-${element}`
        : `sliderfullwidthgallery-${cmsIndex}`
      : undefined;

  const updateNavState = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const totalSlides = items.length;

  if (totalSlides === 0) {
    return null;
  }

  const progress = (1 + swiperProgress * (totalSlides - 1)) / totalSlides;

  const breakpoints = {
    0: {
      slidesPerView: 1.2,
      spaceBetween: 12,
    },
    640: {
      slidesPerView: 2.2,
      spaceBetween: 20,
    },
    768: {
      slidesPerView: 2.5,
      spaceBetween: 24,
    },
    1024: {
      slidesPerView: 2.5,
      spaceBetween: 24,
    },
  };

  return (
    <section
      ref={containerRef as React.RefObject<HTMLElement>}
      className={clsx(styles.wrapper, className)}
      data-testid={testId()}
    >
      <Swiper
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          updateNavState(swiper);
        }}
        onSlideChange={updateNavState}
        onProgress={(_, p) => setSwiperProgress(p)}
        breakpoints={breakpoints}
        spaceBetween={16}
        className={styles.swiper}
      >
        {items.map((item, index) => {
          const typed = item as {
            __typename?: string;
            sys?: { id?: string };
            entryEffect?: boolean;
          };
          const sysId = typed?.sys?.id;
          const isImage = typed.__typename === 'ImageWithFocalPoint';
          const hasEffect = effectIndices.has(index);
          const revealed = isRevealed(index);

          const slideContent = isImage ? (
            <MediaWrapper
              media={{
                image: (item as CMSImage).image,
                mobileImage: (item as CMSImage).imageMobile,
                video: (item as CMSImage).video,
                mobileVideo: (item as CMSImage).videoMobile,
              }}
              alt={(item as CMSImage).alt || (item as CMSImage).image?.title || ''}
              className={styles.galleryItem}
            />
          ) : (
            <Card
              type={(item as ContentfulCardFragment).type as never}
              data={item as ContentfulCardFragment}
              className={styles.galleryItem}
              cmsIndex={index}
            />
          );

          return (
            <SwiperSlide
              key={sysId || index}
              className={styles.slide}
              data-testid={testId(`slide-${index}`)}
            >
              {hasEffect ? (
                <div
                  className={clsx(styles.slideFill, styles.entryEffect, revealed && styles.visible)}
                >
                  {slideContent}
                </div>
              ) : (
                slideContent
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div className={styles.controls}>
        <div
          className={styles.scrollbar}
          style={{ '--progress': progress } as React.CSSProperties}
        />
        <div className={styles.arrows}>
          <button
            type="button"
            className={clsx(styles.arrowBtn, isBeginning && styles.arrowDisabled)}
            aria-disabled={isBeginning}
            aria-label={aria.action('previousSlide')}
            onClick={() => swiperInstance?.slidePrev()}
            data-testid={testId('prev')}
          >
            <Icon name="chevron-left" size={20} />
          </button>
          <button
            type="button"
            className={clsx(styles.arrowBtn, isEnd && styles.arrowDisabled)}
            aria-disabled={isEnd}
            aria-label={aria.action('nextSlide')}
            onClick={() => swiperInstance?.slideNext()}
            data-testid={testId('next')}
          >
            <Icon name="chevron-right" size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}

SliderFullwidthGallery.displayName = 'SliderFullwidthGallery';
